/**
 * ABOUTME: Simple analytics tracking utilities for the application.
 * ABOUTME: Provides functions to track events, page views, and conversions.
 */

import { debug } from './utils';

type EventParams = Record<string, string | number | boolean | undefined>;

/**
 * Track an event in the analytics system
 * @param eventName - The name of the event to track
 * @param params - Additional parameters to include with the event
 */
export function trackEvent(eventName: string, params?: EventParams): void {
  // Simple debug logging - in a real implementation this would send to an analytics service
  debug.log(`[ANALYTICS] Event: ${eventName}`, params);

  // Check if window.gtag is available (for Google Analytics)
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, params);
    } catch (error) {
      debug.error('[ANALYTICS] Error tracking event:', error);
    }
  }

  // Check if window.plausible is available (for Plausible Analytics)
  if (typeof window !== 'undefined' && window.plausible) {
    try {
      window.plausible(eventName, { props: params });
    } catch (error) {
      debug.error('[ANALYTICS] Error tracking plausible event:', error);
    }
  }
}

/**
 * Track a page view in the analytics system
 * @param pageName - The name of the page being viewed
 * @param path - The path of the page
 */
export function trackPageView(pageName: string, path?: string): void {
  trackEvent('page_view', {
    page_title: pageName,
    page_path: path || window.location.pathname,
  });
}

/**
 * Track a conversion event
 * @param conversionName - The name of the conversion
 * @param value - The value of the conversion
 */
export function trackConversion(conversionName: string, value?: number): void {
  trackEvent('conversion', {
    conversion_name: conversionName,
    value,
  });
}

// Add typings for global analytics objects
declare global {
  interface Window {
    gtag?: (command: string, action: string, params?: Record<string, unknown>) => void;
    plausible?: (eventName: string, options?: { props?: EventParams }) => void;
  }
}
